import {
  Button,
  Field,
  HStack,
  IconButton,
  Popover,
  Portal,
  SimpleGrid,
  Text,
} from '@chakra-ui/react'
import { useState } from 'react'
import { LuCalendar, LuChevronLeft, LuChevronRight } from 'react-icons/lu'
import {
  EFFECTIVE_DATE_MONTHS_AHEAD,
  MONTH_LABELS_DE_SHORT,
  earliestEffectiveDate,
  effectiveDateProblemMessage,
  formatIsoDateDE,
  formatMonthYearDE,
  toIsoDate,
  todayInBusinessZone,
  validateEffectiveDate,
  type IsoDate,
} from '@/helper/valueChange.helper'

type EffectiveDatePickerProps = {
  /** ISO calendar date, or '' while nothing is chosen. */
  value: IsoDate | ''
  onChange: (value: IsoDate | '') => void
  /** Whether a date is needed for the current input, i.e. the value actually changed. */
  required?: boolean
  disabled?: boolean
  /** Show the validation message even before the user touched the field. */
  showError?: boolean
  label?: string
  /** Today in the business zone; injectable for tests. */
  today?: IsoDate
  name?: string
  helperText?: string
}

/**
 * The "Gültig ab" picker for a price or capacity change (SIB-941).
 *
 * A calendar of months and years, since a version can only start on the first of a month: the
 * trigger shows the chosen date, the popover shows one year at a time with its twelve months, and
 * every month that is today's or earlier is disabled. Composed from Chakra's Popover and Button
 * because Chakra has no date picker of its own; no third-party calendar styling is involved.
 */
export function EffectiveDatePicker({
  value,
  onChange,
  required = false,
  disabled = false,
  showError = false,
  label = 'Gültig ab',
  today,
  name,
  helperText,
}: EffectiveDatePickerProps) {
  const todayIso = today ?? todayInBusinessZone()
  const earliest = earliestEffectiveDate(todayIso)
  const earliestYear = Number(earliest.slice(0, 4))
  const latestYear = earliestYear + Math.ceil(EFFECTIVE_DATE_MONTHS_AHEAD / 12)

  const [open, setOpen] = useState(false)
  const [year, setYear] = useState(() => Number((value || earliest).slice(0, 4)))

  const problem = required ? validateEffectiveDate(value, todayIso) : null
  const invalid = !!problem && showError

  function openTo(nextOpen: boolean) {
    if (nextOpen) setYear(Number((value || earliest).slice(0, 4)))
    setOpen(nextOpen)
  }

  function pick(iso: IsoDate) {
    onChange(iso)
    setOpen(false)
  }

  return (
    <Field.Root required={required} invalid={invalid} disabled={disabled}>
      <Field.Label>
        {label} {required ? <Field.RequiredIndicator /> : null}
      </Field.Label>

      <Popover.Root
        open={open}
        onOpenChange={e => openTo(e.open)}
        positioning={{ placement: 'bottom-start' }}
        // Kept mounted after closing: a month button that unmounts on its own click leaves the
        // surrounding drawer with a detached click target, which its outside-click detection
        // would treat as a dismissal.
        lazyMount
      >
        <Popover.Trigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            aria-label={label}
            aria-haspopup="dialog"
            aria-expanded={open}
            data-testid="effective-date-trigger"
          >
            <LuCalendar />
            {value ? formatIsoDateDE(value) : 'Bitte auswählen'}
          </Button>
        </Popover.Trigger>

        <Portal>
          <Popover.Positioner>
            <Popover.Content aria-label={`${label} auswählen`}>
              <Popover.Arrow />
              <Popover.Body>
                <HStack justify="space-between">
                  <IconButton
                    aria-label="Vorheriges Jahr"
                    variant="ghost"
                    onClick={() => setYear(y => y - 1)}
                    disabled={year <= earliestYear}
                  >
                    <LuChevronLeft />
                  </IconButton>
                  <Text aria-live="polite">{year}</Text>
                  <IconButton
                    aria-label="Nächstes Jahr"
                    variant="ghost"
                    onClick={() => setYear(y => y + 1)}
                    disabled={year >= latestYear}
                  >
                    <LuChevronRight />
                  </IconButton>
                </HStack>

                <SimpleGrid columns={3} gap={2} role="group" aria-label={`Monate ${year}`}>
                  {MONTH_LABELS_DE_SHORT.map((short, index) => {
                    const iso = toIsoDate(year, index + 1, 1)
                    const allowed = validateEffectiveDate(iso, todayIso) === null
                    const selected = iso === value
                    return (
                      <Button
                        key={iso}
                        variant={selected ? 'solid' : 'ghost'}
                        disabled={!allowed}
                        aria-label={formatMonthYearDE(iso)}
                        aria-pressed={selected}
                        onClick={() => pick(iso)}
                      >
                        {short}
                      </Button>
                    )
                  })}
                </SimpleGrid>
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>

      {name ? <input type="hidden" name={name} value={value} readOnly /> : null}

      {invalid && problem ? (
        <Field.ErrorText>{effectiveDateProblemMessage(problem, todayIso)}</Field.ErrorText>
      ) : helperText ? (
        <Field.HelperText>{helperText}</Field.HelperText>
      ) : null}
    </Field.Root>
  )
}
